import {useMountEffect} from '@pinecast/common/hooks/useMountEffect';
import type {Message} from '@pinecast/i18n';
import {useIntl} from '@pinecast/i18n';
import * as React from 'react';

const PageTitleContext = React.createContext<
  Array<{message: Message; values?: Record<string, any>}>
>([]);

export const usePageTitle = (message: Message) => {
  const intl = useIntl();
  const titleStack = React.useContext(PageTitleContext);
  useMountEffect(() => {
    document.title = titleStack.reduce(
      (formatted, {message, values}) =>
        intl.formatMessage(message, {...values, subtitle: formatted}),
      intl.formatMessage(message),
    );
  });
};
export const usePageTitleEffect = (
  effect: () => [Message] | [Message, Record<string, any>] | void,
  dependencies: Array<unknown>,
) => {
  const intl = useIntl();
  const titleStack = React.useContext(PageTitleContext);
  React.useEffect(() => {
    const returned = effect();
    if (!returned) {
      return;
    }
    const [message, values] = returned;
    document.title = titleStack.reduce(
      (formatted, stackItem) =>
        intl.formatMessage(stackItem.message, {
          subtitle: formatted,
          ...stackItem.values,
        }),
      intl.formatMessage(message, values),
    );
  }, [effect, titleStack, ...dependencies]); // eslint-disable-line react-hooks/exhaustive-deps
};

export const PageTitleProvider = ({
  children,
  message,
  values,
}: {
  children: React.ReactNode;
  message: Message;
  values?: Record<string, any>;
}) => {
  const titleStack = React.useContext(PageTitleContext);
  return (
    <PageTitleContext.Provider value={[{message, values}, ...titleStack]}>
      {children}
    </PageTitleContext.Provider>
  );
};
